import { makeAutoObservable } from 'mobx';
import { SortType, handleSort } from '../../../constants/tool-order';
import { BusinessSummaryData, DailyBusinessData, PaymentMethodItem } from '../../../models/fixed/data-analysis';
import { Order } from '../../../models/order.entity';
import { Dish } from '../../../models/dish.entity';
import apiManage from '../../../request';
import { WhenError } from '../../../request/http';
import { FetchCancelGate } from '../../../utils/fetch-cancel-gate';
import i18n from '../../../i18n';
import GlobalNotification from '../../../component/common/snackbar';
import { exportExcel } from '../../../constants/tool.fn';
import { formatDateWithWeekday } from '../../../constants/utils';
import { formatTotalTax, formatTotalWithoutTax } from './shared';


/**
 * 日营业汇总
 */
class DailyBusinessSummaryData {

    constructor() {
        makeAutoObservable(this);
    }

    // 加载用
    loading: boolean = false;
    // 表格数据
    data: DailyBusinessData = {
        businessSummary: []
    };
    // 详情弹窗
    isOpenOrderDetail = false
    // 详情数据
    orderDetailData: Order = {}
    // 详情菜品
    orderDetailDish: Dish[] = []
    // 详情loading
    detailLoading = true
    // 错误信息
    errorMessage = ''
    // 排序
    sortKeyValue: {
        // 日营业分析汇总
        businessSummary?: SortType;
    } = {}
    // 源数据
    dataSource: DailyBusinessData = {
        // 已结堂食订单
        businessSummary: [],
    };
    // 求和数组（与表头列一一对应，含动态支付方式列）
    summationArray: (string | number)[] = [];
    /** 支付方式列（从数据中合并去重，按 tender_media_id 排序，用于表头与合计） */
    paymentMethodColumns: PaymentMethodItem[] = [];
    /** 列表请求取消门闩（日期快速切换时 cancel 上一请求） */
    private listFetchGate = new FetchCancelGate();

    /**
     * 从日营业汇总数据中合并出所有支付方式列（前后两条的 payment_methods 可能不一致，需合并去重后按 tender_media_id 排序）
     */
    static getPaymentMethodColumnsFromData(rows: BusinessSummaryData[]): PaymentMethodItem[] {
        const map = new Map<number, PaymentMethodItem>();
        rows.forEach(row => {
            (row.payment_methods ?? []).forEach(pm => {
                if (!map.has(pm.tender_media_id)) {
                    map.set(pm.tender_media_id, { ...pm });
                }
            });
        });
        return Array.from(map.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([, v]) => v);
    }


    /**
     * 获取数据
     */
    async fetchData(stringId: string, startDate: string, endDate: string) {
        const { cancelToken, seq } = this.listFetchGate.begin();
        this.setLoading(true);
        try {
            const res = await apiManage.get_data_analysis_res_business_summary_every_day({
                string_id: stringId,
                start: startDate,
                end: endDate
            }, { cancelToken });
            if (!this.listFetchGate.isLatest(seq)) {
                return;
            }
            if (res && !res?.error) {
                this.setData(res);
                const paymentColumns = DailyBusinessSummaryData.getPaymentMethodColumnsFromData(res);
                this.setPaymentMethodColumns(paymentColumns);
                this.setSummationArr(this.calculateDataTotal(res, paymentColumns));
                this.setErrorMessage('');
            } else {
                const error = res as WhenError;
                if (error.message !== 'canceled') {
                    if (error?.result?.statusCode === 404) {
                        this.setErrorMessage(i18n.t('business_analysis_page_text_restaurant_data_not_synchronized'));
                    } else {
                        GlobalNotification.error(i18n.t('daily_business_summary_page_text_failed_to_obtain_daily_business_summary'));
                    }
                    this.setData([]);
                    this.setPaymentMethodColumns([]);
                }
            }
        } finally {
            this.listFetchGate.endLoading(seq, (value) => this.setLoading(value));
        }
    }

    /**
     * 计算合计值（固定列 + 动态支付方式列，与表头一一对应）
     * 单次遍历累加，避免多次 reduce 带来的性能损耗
     */
    calculateDataTotal(res: BusinessSummaryData[], paymentColumns: PaymentMethodItem[]) {
        const paymentMap = new Map<number, number>();
        paymentColumns.forEach(pc => paymentMap.set(pc.tender_media_id, 0));

        const acc = {
            total_customers: 0,
            open_tables: 0,
            total_actual_amount: 0,
            eat_in_actual_amount_total: 0,
            take_out_actual_amount_total: 0,
            eat_in_order_total: 0,
            take_out_order_total: 0,
            total_discount: 0,
            total_tax: 0,
            total_without_tax: 0,
            total_service_amount: 0,
            total_service_tax: 0,
            total_tip_amount: 0,
            total_tip_tax: 0,
        };

        res.forEach(row => {
            acc.total_customers += Number(row.total_customers ?? 0);
            acc.open_tables += Number(row.open_tables ?? 0);
            acc.total_actual_amount += Number(row.total_actual_amount ?? 0);
            acc.eat_in_actual_amount_total += Number(row.eat_in_actual_amount_total ?? 0);
            acc.take_out_actual_amount_total += Number(row.take_out_actual_amount_total ?? 0);
            acc.eat_in_order_total += Number(row.eat_in_order_total ?? 0);
            acc.take_out_order_total += Number(row.take_out_order_total ?? 0);
            acc.total_discount += Number(row.total_discount ?? 0);
            acc.total_tax += formatTotalTax(row);
            acc.total_without_tax += formatTotalWithoutTax(row);
            acc.total_service_amount += Number(row.total_service_amount ?? 0);
            acc.total_service_tax += Number(row.total_service_tax ?? 0);
            acc.total_tip_amount += Number(row.total_tip_amount ?? 0);
            acc.total_tip_tax += Number(row.total_tip_tax ?? 0);
            (row.payment_methods ?? []).forEach(pm => {
                const cur = paymentMap.get(pm.tender_media_id) ?? 0;
                paymentMap.set(pm.tender_media_id, cur + Number(pm?.amount ?? 0));
            });
        });

        const summationArr: (string | number)[] = [
            '-',
            acc.total_customers.toString(),
            acc.open_tables.toString(),
            acc.total_actual_amount.toFixed(2),
            acc.eat_in_actual_amount_total.toFixed(2),
            acc.take_out_actual_amount_total.toFixed(2),
            acc.eat_in_order_total.toString(),
            acc.take_out_order_total.toString(),
            acc.total_discount.toFixed(2),
            acc.total_tax.toFixed(2),
            acc.total_without_tax.toFixed(2),
            acc.total_service_amount.toFixed(2),
            acc.total_service_tax.toFixed(2),
            acc.total_tip_amount.toFixed(2),
            acc.total_tip_tax.toFixed(2),
            ...paymentColumns.map(pc => (paymentMap.get(pc.tender_media_id) ?? 0).toFixed(2)),
        ];
        return summationArr;
    }

    /**
     * 使用当前内存中的日营业行（与列表/排序一致）生成 xlsx，不重复请求接口
     */
    downloadExcel() {
        const downloadData = this.data.businessSummary ?? [];
        if (!downloadData.length) {
            GlobalNotification.warning(i18n.t('general_text_no_data'));
            return;
        }
        const { newData, wpx } = this.buildDownloadSheetData(downloadData);
        exportExcel(newData, `${i18n.t('daily_business_summary_page_text_daily_business_summary')}.xlsx`, wpx);
    }

    /**
     * 构建下载的表的数据（表头与数据列含动态支付方式）
     */
    buildDownloadSheetData(downloadData: BusinessSummaryData[]) {
        const paymentColumns = DailyBusinessSummaryData.getPaymentMethodColumnsFromData(downloadData);
        const totalCols = 16 + paymentColumns.length;
        const wpx = new Array(totalCols).fill({ wpx: 90 });

        const headRow: string[] = [
            i18n.t('table_component_text_serial_number'),
            i18n.t('business_hour_page_date'),
            i18n.t('immediate_number_people'),
            i18n.t('daily_business_summary_page_table_opening'),
            i18n.t('business_analysis_page_text_actual_amount_total'),
            i18n.t('business_analysis_page_text_hall_amount_total'),
            i18n.t('business_analysis_page_text_takeout_amount_total'),
            i18n.t('business_analysis_page_text_dine_in_table_nums'),
            i18n.t('business_analysis_page_text_takeout_total'),
            i18n.t('daily_business_summary_page_discount'),
            i18n.t('daily_business_summary_page_should_pay_taxes_total_amount'),
            i18n.t('daily_business_summary_page_gross_untaxed'),
            i18n.t('business_analysis_page_text_service_charges'),
            i18n.t('manager_text_total_taxable_service_fee'),
            i18n.t('business_analysis_page_text_tip'),
            i18n.t('manager_text_total_taxable_tip'),
            ...paymentColumns.map(p => p.tender_media_name),
        ];
        const headData: string[][] = [headRow];

        const buildTableData: string[][] = downloadData.map((item, index) => {
            const date = formatDateWithWeekday(item.order_date || '');
            const pmMap = new Map((item.payment_methods ?? []).map(p => [p.tender_media_id, Number(p.amount ?? 0)]));
            const paymentAmounts = paymentColumns.map(pc => (pmMap.get(pc.tender_media_id) ?? 0).toFixed(2));
            return [
                String(index + 1),
                date,
                item?.total_customers || '',
                String(item?.open_tables) || '',
                Number(item?.total_actual_amount ?? 0).toFixed(2),
                Number(item?.eat_in_actual_amount_total ?? 0).toFixed(2),
                Number(item?.take_out_actual_amount_total ?? 0).toFixed(2),
                String(Number(item?.eat_in_order_total ?? 0)),
                String(Number(item?.take_out_order_total ?? 0)),
                Number(item?.total_discount ?? 0).toFixed(2),
                formatTotalTax(item).toFixed(2),
                formatTotalWithoutTax(item).toFixed(2),
                Number(item.total_service_amount ?? 0).toFixed(2),
                Number(item.total_service_tax ?? 0).toFixed(2),
                Number(item.total_tip_amount ?? 0).toFixed(2),
                Number(item.total_tip_tax ?? 0).toFixed(2),
                ...paymentAmounts,
            ];
        });

        const sumTotals = this.calculateDataTotal(downloadData, paymentColumns);
        sumTotals.unshift(i18n.t('table_component_text_total'));
        buildTableData.push(sumTotals.map((v) => String(v)));

        return {
            newData: headData.concat(buildTableData),
            wpx,
        };
    }

    /**
     * 更新数据
     * @param value
     */
    setData(value: BusinessSummaryData[]) {
        this.data.businessSummary = value;
        this.dataSource.businessSummary = value;
    }

    /** 设置支付方式列（表头与合计用） */
    setPaymentMethodColumns(value: PaymentMethodItem[]) {
        this.paymentMethodColumns = value;
    }

    // 设置求和数组
    setSummationArr(resVal: typeof this.summationArray) {
        this.summationArray = resVal;
    }

    /**
     * 更新loading
     * @param value
     */
    setLoading(value: boolean) {
        this.loading = value
    }

    /**
     * 打开详情弹窗
     * @param value
     */
    setOpenOrderDetail(value: boolean) {
        this.isOpenOrderDetail = value
    }

    /**
     * 详情数据
     * @param value
     */
    setOrderDetailData(value: Order) {
        this.orderDetailData = value
    }

    /**
     * 详情菜品
     * @param value
     */
    setOrderDetailDish(value: Dish[]) {
        this.orderDetailDish = value
    }

    /**
     * 详情loading
     * @param value
     */
    setDetailLoading(value: boolean) {
        this.detailLoading = value
    }

    /**
     * 更新errorMessage
     * @param value
     */
    setErrorMessage(value: string) {
        this.errorMessage = value
    }

    /**
     * 更新排序
     * @param value
     */
    setEatInArrSort(value: SortType, key: keyof DailyBusinessData) {
        this.sortKeyValue[key] = value
    }

    /**
     * 日营业汇总行按支付方式列金额排序（表头 id 为 payment_{tender_media_id}，数据在 payment_methods 中）
     */
    private sortBusinessSummaryByPaymentAmount(
        rows: BusinessSummaryData[],
        sortValue: 'ASC' | 'DESC',
        tenderMediaId: number,
    ): BusinessSummaryData[] {
        const getAmount = (row: BusinessSummaryData) => {
            const pm = (row.payment_methods ?? []).find((p) => p.tender_media_id === tenderMediaId);
            return Number(pm?.amount ?? 0);
        };
        const next = [...rows];
        if (sortValue === 'ASC') {
            return next.sort((a, b) => getAmount(a) - getAmount(b));
        }
        return next.sort((a, b) => getAmount(b) - getAmount(a));
    }

    /**
     * 日营业汇总行按与单元格展示一致的计算值排序（应税/未税与 formatTotal* 一致）
     */
    private sortBusinessSummaryByComputed(
        rows: BusinessSummaryData[],
        sortValue: 'ASC' | 'DESC',
        getNumeric: (row: BusinessSummaryData) => number,
    ): BusinessSummaryData[] {
        const next = [...rows];
        if (sortValue === 'ASC') {
            return next.sort((a, b) => getNumeric(a) - getNumeric(b));
        }
        return next.sort((a, b) => getNumeric(b) - getNumeric(a));
    }

    /**
     * 排序
     * @param key
     */
    setDataSort(key: keyof DailyBusinessData) {
        const value = this.data[key] as BusinessSummaryData[];
        const sortValue = this.sortKeyValue[key]?.value;
        const sortKey = this.sortKeyValue[key]?.key;
        // 是否有排序
        if (sortValue && sortKey) {
            if (sortKey === 'order_date') {
                this.data[key] = handleSort({ value: sortValue, key: sortKey }, [...value], 'Date') || [];
            } else if (sortKey === 'total_tax_amount') {
                // 与表格展示 formatTotalTax 一致
                this.data[key] = this.sortBusinessSummaryByComputed(value, sortValue, (row) => formatTotalTax(row));
            } else if (sortKey === 'total_without_tax_amount') {
                // 与表格展示 formatTotalWithoutTax 一致
                this.data[key] = this.sortBusinessSummaryByComputed(value, sortValue, (row) => formatTotalWithoutTax(row));
            } else if (sortKey.startsWith('payment_')) {
                const tenderMediaId = Number(sortKey.replace('payment_', ''));
                this.data[key] = Number.isFinite(tenderMediaId)
                    ? this.sortBusinessSummaryByPaymentAmount(value, sortValue, tenderMediaId)
                    : handleSort({ value: sortValue, key: sortKey }, [...value], 'Number') || [];
            } else {
                this.data[key] = handleSort({ value: sortValue, key: sortKey }, [...value], 'Number') || [];
            }
        } else {
            // 没有排序 用源数据
            this.data[key] = [...this.dataSource[key]] as BusinessSummaryData[];
        }
    }


}

export default DailyBusinessSummaryData;